import { useState, useRef } from "react";
import { Lock, FileText, ShieldCheck, Loader2, Upload, X } from "lucide-react";
import { api } from "../../lib/api";
import { PROJECT_DOCUMENT_ACCEPT, projectDocumentError } from "../../lib/projectDocuments";
import { useI18n } from "../../lib/i18n";
import { type BidDocument, fmtSize } from "./model";

export function BidForm({
  form, setForm, busy, onSubmit, submitLabel, bidderName, bidderAvailable, legalTerms, legalTermsState, reviewConfirmed, onReviewConfirmed,
  files, onFilesChange, existingDocuments, onRemoveDocument,
}: {
  form: { price: string; durationText: string; terms: string };
  setForm: (f: any) => void;
  busy: boolean;
  onSubmit: (e: React.FormEvent) => void;
  submitLabel: string;
  bidderName: string;
  bidderAvailable: boolean;
  legalTerms: Awaited<ReturnType<typeof api.legalDoc>> | null;
  legalTermsState: "loading" | "ready" | "unavailable";
  reviewConfirmed: boolean;
  onReviewConfirmed: (confirmed: boolean) => void;
  files: File[];
  onFilesChange: (files: File[]) => void;
  existingDocuments: BidDocument[];
  onRemoveDocument: (documentId: string) => void;
}) {
  const { t } = useI18n();
  const set = (k: string, v: any) => setForm((f: any) => ({ ...f, [k]: v }));
  const documentInput = useRef<HTMLInputElement>(null);
  const [fileError, setFileError] = useState<string | null>(null);
  const documentCount = existingDocuments.length + files.length;
  function selectDocuments(selected: FileList | null) {
    const picked = Array.from(selected || []);
    const invalidDocument = picked.map(projectDocumentError).find((message) => message !== null);
    if (invalidDocument) { setFileError(invalidDocument); return; }
    const remaining = Math.max(0, 5 - documentCount);
    onFilesChange([...files, ...picked.slice(0, remaining)]);
    setFileError(picked.length > remaining ? "A bid can have up to 5 documents." : null);
  }
  return (
    <form onSubmit={onSubmit} className="space-y-3 mt-3">
      <div><label className="label">Proposed Price (PHP)</label><input className="field" inputMode="numeric" value={form.price} onChange={(e) => set("price", e.target.value)} placeholder="e.g. 5,800,000" /></div>
      <div><label className="label">Proposed Completion Period</label><input className="field" value={form.durationText} onChange={(e) => set("durationText", e.target.value)} placeholder="e.g. 150 days" /></div>
      <div><label className="label">{t("bid.termsExclusions")}</label><textarea className="field h-20 py-2" value={form.terms} onChange={(e) => set("terms", e.target.value)} placeholder="Notable inclusions/exclusions…" /></div>
      <div className="rounded-lg border border-line bg-navy-50/40 p-3">
        <div className="flex flex-wrap items-center justify-between gap-2">
          <div>
            <label className="block text-xs font-bold text-navy" htmlFor="bid-documents">Supporting documents <span className="font-normal text-mut">(optional)</span></label>
            <p className="mt-0.5 text-[11px] text-mut">The client can download these with your bid.</p>
          </div>
          <span className="text-xs font-semibold text-mut">{documentCount}/5 files</span>
        </div>
        <input ref={documentInput} id="bid-documents" type="file" multiple accept={PROJECT_DOCUMENT_ACCEPT} className="sr-only"
          disabled={busy || documentCount >= 5} aria-describedby="bid-document-help"
          onChange={(event) => { selectDocuments(event.target.files); event.target.value = ""; }} />
        <button type="button" className="btn-outline h-9 px-3 mt-3 text-xs" disabled={busy || documentCount >= 5}
          onClick={() => documentInput.current?.click()}><Upload className="h-4 w-4" />Add documents</button>
        <p id="bid-document-help" className="mt-2 text-[11px] text-mut">PDF or JPG, PNG, and WebP images · 25 MB per file</p>
        {fileError && <p role="alert" className="mt-2 text-xs text-brand-red">{fileError}</p>}
        {documentCount > 0 && <ul className="mt-3 space-y-2 border-t border-line pt-3">
          {existingDocuments.map((document) => <li key={document.id} className="flex min-w-0 items-center gap-2 rounded-md bg-white px-2.5 py-2 text-xs text-navy">
            <FileText className="h-4 w-4 shrink-0" /><span className="min-w-0 flex-1 truncate" title={document.filename}>{document.filename}</span>
            <span className="shrink-0 text-[11px] text-mut">Uploaded</span>
            <button type="button" disabled={busy} className="shrink-0 rounded p-1 text-brand-red hover:bg-red-50" onClick={() => onRemoveDocument(document.id)} aria-label={`Remove ${document.filename}`}><X className="h-4 w-4" /></button>
          </li>)}
          {files.map((file, index) => <li key={`${file.name}-${file.lastModified}-${index}`} className="flex min-w-0 items-center gap-2 rounded-md bg-white px-2.5 py-2 text-xs text-navy">
            <FileText className="h-4 w-4 shrink-0" /><span className="min-w-0 flex-1 truncate" title={file.name}>{file.name}</span>
            <span className="shrink-0 text-[11px] text-mut">{fmtSize(file.size)}</span>
            <button type="button" disabled={busy} className="shrink-0 rounded p-1 text-brand-red hover:bg-red-50" onClick={() => onFilesChange(files.filter((_, fileIndex) => fileIndex !== index))} aria-label={`Remove selected ${file.name}`}><X className="h-4 w-4" /></button>
          </li>)}
        </ul>}
      </div>
      <div>
        <h3 className="text-sm font-bold text-navy mb-1">Profile shared with the client</h3>
        <p className="rounded-lg border border-line bg-navy-50/60 p-3 text-xs leading-relaxed text-mut">The client sees your bidder name ({bidderName}), sealed price, duration, and terms, and can open your member profile. Other providers cannot see your bid.</p>
      </div>
      <details className="rounded-lg border border-line bg-navy-50/60 p-3 text-xs text-mut">
        <summary className="cursor-pointer font-bold text-navy">Read published Terms of Service</summary>
        {legalTermsState === "loading" && <p className="mt-2">Loading terms…</p>}
        {legalTermsState === "unavailable" && <p className="mt-2 text-brand-red">The published Terms of Service are unavailable. Please try again later.</p>}
        {legalTerms && <div className="mt-2 max-h-48 overflow-y-auto whitespace-pre-wrap leading-relaxed">{legalTerms.body}</div>}
      </details>
      <label className="flex items-start gap-2 text-xs text-mut leading-relaxed">
        <input type="checkbox" required disabled={!legalTerms} checked={reviewConfirmed} onChange={(event) => onReviewConfirmed(event.target.checked)} className="mt-0.5 shrink-0" />
        <span>I reviewed the Terms of Service before submitting this bid.</span>
      </label>
      <button disabled={busy || !bidderAvailable || legalTermsState !== "ready" || !legalTerms} className="btn-red w-full">{busy ? <Loader2 className="w-4 h-4 animate-spin" /> : <Lock className="w-4 h-4" />}{submitLabel}</button>
      <p className="text-[11px] text-mut flex items-center gap-1 justify-center"><ShieldCheck className="w-3 h-3" />Escrow funding follows an accepted contract offer.</p>
    </form>
  );
}
